import { useState } from 'react'
import toast from 'react-hot-toast'

import {
  composeQuoteCarrier,
  FvButton,
  FvNum,
  useFvNavigate,
} from '@fv/client-components'
import { showApiError } from '@fv/client-core'
import type { Quote } from '@fv/client-types'
import type { QuoteReasonPayload } from '@fv/models'

import { Loading } from '@/components/Loading'
import {
  ShipperSlider,
  type ShipperSliderProps,
} from '@/components/ShipperSlider'
import { useLoad, useQuotes } from '@/hooks/shipments'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'
import { routes } from '@/routes'

import { HasPermission } from '../auth'
import { loadLocationContactPhoneRequired } from '../book-eligibility/bookEligibility'
import { toLocalCalendarDateFromUtc } from '../dates/dateRange'
import { useVendorBookRate } from '../quote/useBookRate'
import { useNeedsQuoteReason } from '../quote-reason/hooks'
import { QuoteReasonFormControls } from '../quote-reason/QuoteReasonFormControls'
import { getBookActionLabel } from '../shipment-form/utils'
import { useCanSchedulePickup } from './useCanSchedulePickup'

const isValidationError = (e: unknown): e is { name: string } =>
  typeof e === 'object' &&
  e !== null &&
  'name' in e &&
  e.name === 'VALIDATION_ERROR'

type Props = ShipperSliderProps & {
  loadId: string
  quote: Quote
}
export const VendorBookPanel = ({
  loadId,
  quote,
  onClose,
  ...props
}: Props) => {
  const navigate = useFvNavigate()
  const loadQuery = useLoad(loadId)
  const load = loadQuery.data
  const carrierSettingsQuery = useCarrierPublicSettings(quote.carrierId)
  const quotes = useQuotes(loadId)
  const needsQuoteReason = useNeedsQuoteReason()(quotes.data ?? [], quote)
  const isParcel = load?.workflow === 'parcel'
  const [quoteReason, setQuoteReason] = useState<
    QuoteReasonPayload | undefined
  >({ reasonType: 'urgent' })
  const canSchedulePickup = useCanSchedulePickup({
    isFreightCollect: load.isFreightCollect,
    workflow: load.workflow,
    locations: load.locations?.map(({ type, stopDate }) => ({
      type,
      stopDate: toLocalCalendarDateFromUtc(stopDate) ?? null,
    })),
    items: load.items,
    customerRoutedCarrierId: quote.carrierId,
    selectedQuote: quote,
  })
  const carrierSettings = carrierSettingsQuery.carrierPublicSettings
  const requireOriginEmail = carrierSettings?.requiresOriginEmail
  const vendorBookRate = useVendorBookRate()
  const bookLabel = getBookActionLabel(load.workflow)

  const handleSubmit = (schedulePickup: boolean) => async () => {
    const origin = load.locations.find(l => l.type === 'origin')
    const needsOriginEmail = !origin.contactEmail && requireOriginEmail
    const needsContactPhone = load.locations.some(
      l =>
        loadLocationContactPhoneRequired({
          type: l.type,
          shipType: l.shipType,
          accessorials: l.accessorials.map(a => a.key),
        }) && !l.contactPhone,
    )
    if (needsOriginEmail || needsContactPhone) {
      const missingFields = []
      if (needsOriginEmail) missingFields.push('origin contact email')
      if (needsContactPhone) missingFields.push('contact phone')
      toast.error(
        `Please provide ${missingFields.join(' and ')} to complete booking`,
      )
      navigate(routes.vendorBook(loadId, quote._id))
      return
    }

    if (needsQuoteReason && !quoteReason?.reasonType) {
      toast.error("Please indicate why you haven't chosen the cheapest quote")
      return
    }

    try {
      await vendorBookRate.mutateAsync({
        loadId,
        quoteId: quote._id,
        schedulePickup,
        ...(needsQuoteReason && { quoteReason }),
      })
      navigate(routes.details(loadId))
    } catch (e) {
      if (isValidationError(e)) {
        navigate(routes.vendorBook(loadId, quote._id))
      }

      showApiError('Unable to book shipment', e)
    }
  }

  if (!load || carrierSettingsQuery.isFetching) {
    return (
      <ShipperSlider isOpen={props.isOpen} onClose={onClose}>
        <Loading />
      </ShipperSlider>
    )
  }

  return (
    <ShipperSlider {...props} onClose={onClose}>
      <p>
        You're about to book this shipment with{' '}
        <strong>{composeQuoteCarrier({ quote })}</strong> with their{' '}
        <strong>{quote.serviceDescription}</strong> service
        <HasPermission name="view-rates">
          {' '}
          for{' '}
          <strong>
            <FvNum val={quote.amount} currency={quote.currency} />
          </strong>
        </HasPermission>
        . {!needsQuoteReason && <>Are you sure you want to continue?</>}
      </p>
      {needsQuoteReason && (
        <>
          <p>Please indicate why you haven't chosen the cheapest quote:</p>
          <QuoteReasonFormControls
            value={quoteReason}
            onChange={setQuoteReason}
          />
        </>
      )}
      <div className="flex items-center">
        {isParcel && (
          <FvButton
            theme="primary"
            icon="truck"
            onClick={handleSubmit(load.schedulePickup)}
            loading={vendorBookRate.isPending}
          >
            {bookLabel}
          </FvButton>
        )}

        {!isParcel && carrierSettings.allowBolOnly && (
          <>
            <FvButton
              theme="default"
              icon="file-alt"
              onClick={handleSubmit(false)}
              loading={vendorBookRate.isPending}
            >
              Print BOL
            </FvButton>
            <span>&nbsp;- or -&nbsp;</span>
          </>
        )}
        {!isParcel && canSchedulePickup && (
          <FvButton
            theme="primary"
            icon="truck"
            onClick={handleSubmit(true)}
            loading={vendorBookRate.isPending}
          >
            {bookLabel}
          </FvButton>
        )}
      </div>
    </ShipperSlider>
  )
}
